/**
 * Sub-agent chat slice.
 *
 * Owns per-sub-agent chat state: active/revealed sub-agents, cached
 * session ids + messages, the moodboard session shortcut, the published
 * session list, and the transient per-agent busy flags.
 */

import type { StoreApi } from "zustand";
import type { AdsWorkspaceState } from "../useAdsWorkspaceStore.types";

type SetState = StoreApi<AdsWorkspaceState>["setState"];
type GetState = StoreApi<AdsWorkspaceState>["getState"];

export type SubAgentChatActions = Pick<
  AdsWorkspaceState,
  | "setActiveSubAgent"
  | "revealSubAgent"
  | "setSubAgentSessionId"
  | "setSubAgentSessionCreatedAt"
  | "cacheSubAgentMessages"
  | "clearSubAgentSession"
  | "setSubAgentBusy"
  | "setCloneMoodboardSessionId"
  | "setSubAgentSessionList"
>;

export function createSubAgentChatSlice(
  set: SetState,
  _get: GetState,
): SubAgentChatActions {
  return {
    setActiveSubAgent: (agentId) =>
      set((state) => ({
        activeSubAgent: agentId,
        // Also reveal the agent so it stays visible in RunningAgentsPanel
        revealedSubAgents:
          agentId && !state.revealedSubAgents.includes(agentId)
            ? [...state.revealedSubAgents, agentId]
            : state.revealedSubAgents,
      })),

    revealSubAgent: (agentId) =>
      set((state) => ({
        revealedSubAgents: state.revealedSubAgents.includes(agentId)
          ? state.revealedSubAgents
          : [...state.revealedSubAgents, agentId],
      })),

    setSubAgentSessionId: (agentId, sessionId) =>
      set((state) => ({
        subAgentChats: {
          ...state.subAgentChats,
          [agentId]: { ...state.subAgentChats[agentId], sessionId },
        },
      })),

    setSubAgentSessionCreatedAt: (agentId, createdAt) =>
      set((state) => {
        if (state.subAgentChats[agentId].sessionCreatedAt === createdAt) return state;
        return {
          subAgentChats: {
            ...state.subAgentChats,
            [agentId]: { ...state.subAgentChats[agentId], sessionCreatedAt: createdAt },
          },
        };
      }),

    cacheSubAgentMessages: (agentId, messages) =>
      set((state) => ({
        subAgentChats: {
          ...state.subAgentChats,
          [agentId]: { ...state.subAgentChats[agentId], cachedMessages: messages },
        },
      })),

    clearSubAgentSession: (agentId) =>
      set((state) => ({
        subAgentChats: {
          ...state.subAgentChats,
          [agentId]: { sessionId: null, sessionCreatedAt: null, cachedMessages: [] },
        },
      })),

    setSubAgentBusy: (agentId, busy) =>
      set((state) => {
        if (state.subAgentBusy[agentId] === busy) return state;
        return {
          subAgentBusy: { ...state.subAgentBusy, [agentId]: busy },
        };
      }),

    setCloneMoodboardSessionId: (sessionId) =>
      set((state) => {
        if (!sessionId) return state;
        if (state.subAgentChats.moodboard.sessionId === sessionId) return state;
        return {
          subAgentChats: {
            ...state.subAgentChats,
            moodboard: { ...state.subAgentChats.moodboard, sessionId },
          },
        };
      }),

    setSubAgentSessionList: (agentId, sessions) =>
      set((state) => ({
        subAgentSessionList: {
          ...state.subAgentSessionList,
          [agentId]: sessions,
        },
      })),
  };
}
